import { useOktaAuth } from '@okta/okta-react'
import { legit } from 'apex-legit-service'
import { ApexAuth, AuthorizationRequestData } from 'apex-ui-services'
import isNil from 'lodash.isnil'
import React, { createContext, useEffect } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  ALL_REQUESTED_PERMISSIONS,
  convertToAuthRequest,
  LEGIT_SERVICE_CONFIG,
  LOGOUT_REDIRECT_URI,
} from '../../../constants'
import { RootState } from '../../../store'
import { AuthorizationState } from '../../../types'

export interface AuthContextProps {
  handleSignout: () => void
  showAppContent: boolean
}

export interface AuthProviderProps {
  getAuth: (payload: AuthorizationRequestData[]) => void
  auth: AuthorizationState
  showAppContent: boolean
  logout: (params: { apexUser: boolean }) => void
  clearLoginType: () => void
  children: JSX.Element | JSX.Element[]
}

export const AuthContext = createContext<AuthContextProps | {}>({})

export const AuthProviderComponent = ({
  children,
  getAuth,
  auth,
  showAppContent,
  logout,
  clearLoginType,
}: AuthProviderProps) => {
  const { oktaAuth } = useOktaAuth()

  useEffect(() => {
    if (isNil(auth?.authorization) && !auth?.hasBeenAsked) {
      getAuth(convertToAuthRequest(ALL_REQUESTED_PERMISSIONS))
    }
  }, [auth, getAuth])

  const oktaLogout = async () => {
    await oktaAuth.signOut({
      postLogoutRedirectUri: LOGOUT_REDIRECT_URI,
    })
  }

  const handleSignout = () => {
    if (showAppContent) {
      const params = { apexUser: false }
      oktaLogout()
      logout(params)
      clearLoginType()
    }
  }

  return (
    <AuthContext.Provider value={{ handleSignout, showAppContent }}>
      {children}
    </AuthContext.Provider>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getAuth: ApexAuth.authorization.actions.createAuthorization.request,
  logout: (params: { apexUser: boolean }) => {
    dispatch(
      legit.logout(LEGIT_SERVICE_CONFIG, {
        ...params,
      })
    )
  },
  clearLoginType: () => dispatch(ApexAuth.loginType.actions.clearLoginType()),
})

const mapStateToProps = (state: RootState) => {
  return {
    auth: {
      hasBeenAsked: ApexAuth.authorization.selectors.authorizationHasBeenAsked(
        state
      ),
      authorization: ApexAuth.authorization.selectors.authorizations(state),
      error: ApexAuth.authorization.selectors.error(state),
    },
    showAppContent:
      legit.selectors.selectShowLoginUI(state) === legit.LOGIN_UI_HIDE,
  }
}

export const AuthProvider = connect(
  mapStateToProps,
  mapDispatchToProps
)(AuthProviderComponent)
